import { Component, inject, signal, viewChildren } from '@angular/core';
import { formControl, formGroup, Validators } from '@al00x/forms';
import { CategoryEntity } from '@models';
import { AttachmentApiService, AuthStore, CategoryApiService, CategoryTreeComponent, UploadResult } from '@core';
import {
  CacButtonComponent,
  CacFieldComponent,
  CacImageUploaderComponent,
  CacSwitchComponent,
} from '@controladad/ng-base';
import { debounceTime, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { ButtonClickEvent } from '@customer-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-category',
  imports: [
    CategoryTreeComponent,
    CacButtonComponent,
    CacFieldComponent,
    CacSwitchComponent,
    CacImageUploaderComponent,
  ],
  templateUrl: './category.component.html',
  styleUrl: './category.component.scss',
})
export class CategoryComponent {
  private readonly categoryApi = inject(CategoryApiService);
  readonly isRestrictedAdmin = inject(AuthStore).isRestrictedAdmin();

  categoryIdControl = formControl<number>(undefined);
  selectedCategory = signal<CategoryEntity | undefined>(undefined);
  categoryTreeComponents = viewChildren(CategoryTreeComponent);
  categories$ = this.categoryApi.getAll();

  form = formGroup({
    englishName: formControl<string>(undefined, [Validators.required]),
    arabicName: formControl<string>(undefined, Validators.required),
    imagePath: formControl<string | UploadResult | null | undefined>(undefined),
    priority: formControl<number>(0, Validators.required),
    status: formControl<boolean>(true),
    parentCategoryId: formControl<number | null>(null),
  });

  isLoadingCategory = signal(false);

  constructor() {
    this.categoryIdControl.valueChanges
      .pipe(
        tap(() => this.isLoadingCategory.set(true)),
        takeUntilDestroyed(),
        debounceTime(100),
        switchMap((categoryId) => (categoryId ? this.categoryApi.get(categoryId) : of(undefined))),
      )
      .subscribe({
        next: (result) => {
          this.selectedCategory.set(result);
          this.isLoadingCategory.set(false);

          if (result) {
            this.form.patchValue({
              englishName: result.name.english,
              arabicName: result.name.arabic,
              imagePath: result.image?.url,
              priority: result.priority,
              parentCategoryId: result.parentId,
              status: result.status,
            });
          }
        },
        error: () => {
          this.isLoadingCategory.set(false);
        },
      });
  }

  onNewCategory() {
    this.selectedCategory.set(undefined);
    this.form.reset();
    this.categoryIdControl.reset();
  }

  refreshCategories() {
    this.categoryTreeComponents()?.forEach((x) => x.loadCategories());
  }

  onDelete() {
    const categoryId = this.selectedCategory()?.id;
    if (categoryId) {
      dialog$
        .deletePrompt(false, { itemName: this.selectedCategory()?.name.english })
        .action(() => this.categoryApi.delete(categoryId))
        .pipe(take(1))
        .subscribe(() => {
          this.onNewCategory();
          snackbar$.show('The Category Was Deleted Successfully');
          this.refreshCategories();
        });
    } else {
      snackbar$.error('Please Select A Category First');
    }
  }

  onSubmit(e: ButtonClickEvent) {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const categoryId = this.selectedCategory()?.id;
    const values = this.form.getRawValue();

    const fields = {
      name: {
        english: values.englishName,
        arabic: values.arabicName,
      },
      priority: values.priority,
      status: values.status,
      imagePath: this.getImagePath(values.imagePath, this.selectedCategory()?.image?.path),
      parentId: values.parentCategoryId ?? null,
    };

    const request = categoryId
      ? this.categoryApi.update({ categoryId: categoryId, fields: fields })
      : this.categoryApi.create({ fields });

    request.pipe(e.pipe()).subscribe(() => {
      snackbar$.show(`Successfully ${categoryId ? 'Updated' : 'Created'}.`);
      this.refreshCategories();

      if (!categoryId) this.form.reset();
    });
  }

  private getImagePath(
    image: string | UploadResult | undefined | null,
    defaultImage: string | undefined,
  ): string | null {
    if (!image) return null;
    return (typeof image == 'string' ? defaultImage : image.imagePath) ?? null;
  }
}
